import React, { useEffect } from 'react';
import { List, useListRef } from 'react-window';
import { ACCESS_COLUMNS } from '@components/admin/users/study-membership/trialos-study-membership/columns';
import {
    AccessMap,
    aggregateAccess,
    StudyOption,
} from '@components/admin/users/study-membership/trialos-study-membership/helpers';
import StudyAccessRow, {
    AccessCell,
    StudyAccessRowProps,
} from '@components/admin/users/study-membership/trialos-study-membership/study-access-row';
import { useTranslation } from 'hooks';
import { RiskIQAccess } from 'types/graphql-global-types';
import './styles.scss';

export const ROW_HEIGHT = 40;

export interface StudyAccessTableProps {
    studies: StudyOption[];
    original: AccessMap;
    edits: AccessMap;
    disabled: boolean;
    onChangeStudy: (studyId: string, level: RiskIQAccess) => void;
    onChangeAll: (level: RiskIQAccess) => void;
    listHeight?: number;
}

const StudyAccessTable = ({
    studies,
    original,
    edits,
    disabled,
    onChangeStudy,
    onChangeAll,
    listHeight = 400,
}: StudyAccessTableProps) => {
    const { t } = useTranslation();
    const listRef = useListRef(null);
    const studyIds = studies.map((study) => study.id);
    const gridStyle = {
        '--access-grid-columns': `minmax(200px, 1fr) repeat(${ACCESS_COLUMNS.length}, 200px)`,
    } as React.CSSProperties;

    // A new search starts back at the top of the list
    useEffect(() => {
        if (studies.length > 0) listRef.current?.scrollToRow({ index: 0 });
    }, [studies]);

    return (
        <div className="study-access-table" style={gridStyle}>
            <div className="access-grid access-header">
                <div className="access-study" />
                {ACCESS_COLUMNS.map((column) => (
                    <div key={column.key}>{t(column.headerKey)}</div>
                ))}
            </div>
            <div className="access-grid access-all-row">
                <div className="access-study" data-testid="all-studies-label">
                    {t('uam:trialOsStudyMembershipDialog.allStudies', { count: studies.length })}
                </div>
                {ACCESS_COLUMNS.map((column) => (
                    <AccessCell
                        key={column.key}
                        column={column}
                        value={aggregateAccess(studyIds, original, edits)}
                        disabled={disabled || studies.length === 0}
                        testId={`all-${column.key}`}
                        onChange={onChangeAll}
                    />
                ))}
            </div>
            <List<StudyAccessRowProps>
                className="access-list"
                listRef={listRef}
                rowComponent={StudyAccessRow}
                rowCount={studies.length}
                rowHeight={ROW_HEIGHT}
                overscanCount={5}
                defaultHeight={listHeight}
                style={{ height: listHeight }}
                rowProps={{ studies, original, edits, disabled, onChangeStudy }}
            />
        </div>
    );
};

export default StudyAccessTable;
